import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Ban,
  Check,
  Copy,
  Plus,
  Send,
  X,
} from "lucide-react";
import {
  ApiError,
  apiFetch,
  approveAccessRequest,
  createTeamInvite,
  getAdminAccessRequests,
  getAdminTeamConsents,
  getAdminTeams,
  resolveConsentWithdrawal,
  getTeamInvites,
  rejectAccessRequest,
  resendTeamInvite,
  revokeTeamInvite,
} from "@/lib/api";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { useToast } from "@/components/ui/toast";
import { unscopedKey } from "@/lib/queryClient";
import { useMe } from "@/features/auth/useMe";
import type { AccessRequestRow, Player, TeamInviteRow } from "@/lib/types";
import { AdminIconButton } from "@/components/console/AdminIconButton";
import { Button } from "@/components/ui/Button";
import { AdminMetricTile } from "@/components/console/AdminMetricTile";
import { AdminPageHeader } from "@/components/console/AdminPageHeader";
import { useClubOverview } from "../hooks/useAdminData";
import { AdminUsersPage } from "./UsersPage";

/**
 * M8b E1–E8 — `/admin/people` (membership-scoped people console).
 *
 * Capability gate: page hidden unless the active scope has the
 * `membership:manage` capability OR the user is a global admin
 * (the M8a foundation per-section gate prevents direct nav too).
 *
 * Layout: access requests and no-account invites lead the page. Global
 * admins also see the account directory; each account row owns team
 * status and assignment. Pending/no-account invites are
 * row-owned too: the panel loads every team the signed-in admin can
 * manage, each invite row carries `team_id`, and resend/revoke use
 * that row's team rather than a page-level scope switcher.
 */

const INVITE_ROLES = [
  { value: "team_admin", label: "Team admin" },
  { value: "coach", label: "Coach" },
  { value: "assistant_coach", label: "Assistant coach" },
  { value: "player", label: "Player" },
  { value: "guardian", label: "Guardian" },
];

type InviteTeamOption = { id: string; name: string };

function normalizeInviteTeams<T extends InviteTeamOption>(
  teams: T[],
): InviteTeamOption[] {
  const byId = new Map<string, InviteTeamOption>();
  for (const team of teams) byId.set(team.id, { id: team.id, name: team.name });
  return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
}

export function AdminPeoplePage() {
  const me = useMe().data;
  const isGlobalAdmin = !!me?.user?.is_global_admin;
  // Club-wide view: the operator (is_global_admin) OR a club admin on their
  // own host. Both see every team in the club + the full account directory
  // (club-admin parity — `GET /api/admin/teams` and `/api/users` are already
  // host-club-scoped for a club admin). A plain team admin stays scoped to
  // the teams they manage.
  const canSeeClubWide = isGlobalAdmin || !!me?.is_club_admin;
  const adminTeamsQuery = useQuery({
    queryKey: unscopedKey("admin-teams"),
    queryFn: getAdminTeams,
    enabled: canSeeClubWide,
    staleTime: 60_000,
  });
  const manageableTeams = useMemo(
    () =>
      canSeeClubWide
        ? normalizeInviteTeams(adminTeamsQuery.data ?? [])
        : normalizeInviteTeams(
            (me?.teams ?? []).filter((team) =>
              team.capabilities.includes("membership:manage"),
            ),
          ),
    [adminTeamsQuery.data, canSeeClubWide, me?.teams],
  );
  const canManage = !!(canSeeClubWide || manageableTeams.length > 0);
  const accessRequestsQuery = useQuery({
    queryKey: unscopedKey("admin-access-requests"),
    queryFn: getAdminAccessRequests,
    staleTime: 30_000,
    enabled: canManage,
  });
  const pendingAccessCount =
    accessRequestsQuery.data?.filter((row) => row.status === "pending").length ?? 0;
  const overviewQuery = useClubOverview({
    enabled: isGlobalAdmin && canManage,
    refetchIntervalMs: false,
  });
  const linkedTotals = overviewQuery.data?.club?.totals;
  const unlinkedPlayers = linkedTotals
    ? Math.max(0, linkedTotals.active_players - linkedTotals.linked_players)
    : null;

  const pageHeader = (
    <AdminPageHeader
      eyebrow="Club setup"
      title="Access"
      description="Control who can enter the club workspace, which squads they can reach, and which player accounts they are linked to."
    />
  );

  if (!canManage) {
    return (
      <div data-testid="admin-people" className="p-4">
        <p data-testid="admin-people-denied" className="text-destructive text-sm">
          You don't have permission to manage access on this team.
        </p>
      </div>
    );
  }

  const pendingInvites = (
    <section
      id="admin-people-pending-invites"
      data-testid="admin-people-pending-invites"
      className="flex flex-col gap-3"
    >
      <header className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start">
        <div className="min-w-0">
          <h3 className="text-base font-display uppercase tracking-wider text-ink">
            Pending invites
          </h3>
          <p className="mt-1 text-sm text-ink-muted">
            Invite people who do not have accounts yet. Existing accounts can be
            assigned directly from the account directory.
          </p>
        </div>
      </header>

      <InvitesPanel
        teams={manageableTeams}
        teamsLoading={isGlobalAdmin && adminTeamsQuery.isLoading}
      />
    </section>
  );

  return (
    <div data-testid="admin-people" className="flex flex-col gap-4">
      {pageHeader}
      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-5">
        <AdminMetricTile
          label="Pending requests"
          value={String(pendingAccessCount)}
          sub="Self-service joins to review"
          tone={pendingAccessCount > 0 ? "busy" : "good"}
          testid="admin-people-signal-requests"
        />
        <AdminMetricTile
          label="Manageable squads"
          value={String(manageableTeams.length)}
          sub="Access lanes available here"
          tone={manageableTeams.length > 0 ? "good" : "warn"}
          testid="admin-people-signal-squads"
        />
        <AdminMetricTile
          label="Invites"
          value="Open"
          sub="Send, resend, or revoke"
          tone="busy"
          testid="admin-people-signal-invites"
        />
        <AdminMetricTile
          label="Linked players"
          value={
            linkedTotals
              ? `${linkedTotals.linked_player_pct}%`
              : isGlobalAdmin
                ? "Loading"
                : "Scoped"
          }
          sub={
            linkedTotals
              ? `${linkedTotals.linked_players}/${linkedTotals.active_players} active players linked`
              : isGlobalAdmin
                ? "Checking club coverage"
                : "Open account rows for player links"
          }
          tone={
            linkedTotals
              ? unlinkedPlayers && unlinkedPlayers > 0
                ? "warn"
                : "good"
              : "neutral"
          }
          testid="admin-people-signal-linked-players"
        />
        <AdminMetricTile
          label="Directory"
          value={isGlobalAdmin ? "Global" : canSeeClubWide ? "Club" : "Scoped"}
          sub={
            isGlobalAdmin
              ? "Accounts and platform roles"
              : canSeeClubWide
                ? "Club accounts"
                : "Team access only"
          }
          testid="admin-people-signal-directory"
        />
      </div>
      <AccessRequestsPanel />
      {/* The endpoint resolves a CLUB role (`roster:manage` via
          require_club_role), so gate on the same audience the server
          enforces — a plain team admin used to see this panel render
          and then sit permanently on "Failed to load". */}
      {canSeeClubWide ? <TeamConsentsPanel teams={manageableTeams} /> : null}
      {pendingInvites}
      {canSeeClubWide ? <AdminUsersPage embedded /> : null}
    </div>
  );
}

function AccessRequestsPanel() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [target, setTarget] = useState<{
    row: AccessRequestRow;
    action: "approve" | "reject";
  } | null>(null);
  const query = useQuery({
    queryKey: unscopedKey("admin-access-requests"),
    queryFn: getAdminAccessRequests,
    staleTime: 30_000,
  });
  const rows = query.data ?? [];
  const pendingCount = rows.filter((row) => row.status === "pending").length;

  const approve = useMutation({
    mutationFn: (row: AccessRequestRow) => approveAccessRequest(row.id),
    onSuccess: async () => {
      toast.success("Access approved");
      await queryClient.invalidateQueries({ queryKey: unscopedKey("admin-access-requests") });
      await queryClient.invalidateQueries({ queryKey: unscopedKey("admin-users") });
      setTarget(null);
    },
    onError: (err) =>
      toast.error("Couldn't approve request", { description: formatErr(err) }),
  });
  const reject = useMutation({
    mutationFn: (row: AccessRequestRow) => rejectAccessRequest(row.id),
    onSuccess: async () => {
      toast.success("Access rejected");
      await queryClient.invalidateQueries({ queryKey: unscopedKey("admin-access-requests") });
      setTarget(null);
    },
    onError: (err) =>
      toast.error("Couldn't reject request", { description: formatErr(err) }),
  });

  return (
    <section
      id="admin-people-access-requests"
      data-testid="admin-people-access-requests"
      className="flex flex-col gap-3"
    >
      <header className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h3 className="text-base font-display uppercase tracking-wider text-ink">
            Access requests
          </h3>
          <p className="mt-1 text-sm text-ink-muted">
            Review self-service signups that asked to join a team or link to a player.
          </p>
        </div>
        <span className="rounded-full border border-rule bg-elevated px-2 py-1 font-mono text-xs text-ink-muted">
          {pendingCount} pending
        </span>
      </header>
      {query.isError ? (
        <p className="rounded border border-destructive/40 bg-destructive/10 p-3 font-mono text-xs text-destructive">
          Failed to load access requests.
        </p>
      ) : rows.length === 0 ? (
        <p className="rounded border border-rule bg-surface p-3 text-sm text-ink-muted">
          No access requests yet.
        </p>
      ) : (
        <div className="overflow-hidden rounded border border-rule bg-surface">
          <div className="max-h-96 overflow-auto">
            {rows.map((row) => (
              <div
                key={row.id}
                data-testid="admin-access-request-row"
                className="grid gap-3 border-b border-rule px-3 py-3 last:border-b-0 md:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_auto]"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-ink">
                    {row.requester.display_name || row.requester.username}
                    <span className="ml-1 text-ink-muted">
                      @{row.requester.username}
                    </span>
                  </div>
                  <div className="mt-1 font-mono text-xs text-ink-muted">
                    {row.requester.email || "No email"} · {row.created_at}
                  </div>
                  {row.requester_note ? (
                    <p className="mt-1 line-clamp-2 text-xs text-ink-muted">
                      {row.requester_note}
                    </p>
                  ) : null}
                </div>
                <div className="min-w-0">
                  <div className="text-sm text-ink">{row.team.name}</div>
                  <div className="mt-1 font-mono text-xs uppercase tracking-wider text-ink-muted">
                    {row.requested_role.replace("_", " ")}
                    {row.requested_player ? ` · ${row.requested_player.display_name}` : ""}
                  </div>
                  <span
                    data-status={row.status}
                    className="mt-2 inline-flex rounded-full border border-rule px-2 py-0.5 font-mono text-[0.65rem] uppercase tracking-wider text-ink-muted data-[status=approved]:border-good/40 data-[status=approved]:text-good data-[status=pending]:border-accent/40 data-[status=pending]:text-accent data-[status=rejected]:border-flag/40 data-[status=rejected]:text-flag"
                  >
                    {row.status}
                  </span>
                  {row.status === "pending" &&
                  (row.has_consent === false ||
                    row.consent_version_current === false) ? (
                    // Approval refuses on two grounds: no usable affirmation
                    // on the row, or one captured under superseded wording.
                    // Flag BOTH before the click — checking only the first
                    // let a stale request look clean and 409 on Approve.
                    //
                    // Wording is deliberately "check", not "will be refused":
                    // approval also accepts a separately recorded
                    // consent_records row (the operator's offline fallback),
                    // so this is a prompt to verify, not a guaranteed block.
                    <span
                      data-testid="access-request-consent-chip"
                      data-reason={
                        row.has_consent === false ? "missing" : "stale"
                      }
                      title={
                        row.has_consent === false
                          ? "No usable affirmation on this request. Approval is refused unless consent was recorded separately from the roster."
                          : "This affirmation was captured under an older version of the documents. Approval will ask them to re-submit."
                      }
                      className="mt-2 ml-1 inline-flex rounded-full border border-flag/40 px-2 py-0.5 font-mono text-[0.65rem] uppercase tracking-wider text-flag"
                    >
                      {row.has_consent === false
                        ? "Check consent"
                        : "Consent out of date"}
                    </span>
                  ) : null}
                </div>
                <div className="flex items-center gap-1 justify-end">
                  <AdminIconButton
                    icon={Check}
                    label="Approve access request"
                    tone="good"
                    onClick={() => setTarget({ row, action: "approve" })}
                    disabled={row.status !== "pending"}
                    data-testid="admin-access-request-approve"
                  />
                  <AdminIconButton
                    icon={X}
                    label="Reject access request"
                    tone="destructive"
                    onClick={() => setTarget({ row, action: "reject" })}
                    disabled={row.status !== "pending"}
                    data-testid="admin-access-request-reject"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {target ? (
        <ConfirmDialog
          title={target.action === "approve" ? "Approve access?" : "Reject access?"}
          message={
            <p>
              {target.action === "approve" ? "Grant" : "Reject"}{" "}
              <strong>
                {target.row.requester.display_name || target.row.requester.username}
              </strong>
              's request for {target.row.team.name}.
            </p>
          }
          confirmLabel={
            approve.isPending || reject.isPending
              ? target.action === "approve" ? "Approving..." : "Rejecting..."
              : target.action === "approve" ? "Approve" : "Reject"
          }
          confirmDisabled={approve.isPending || reject.isPending}
          danger={target.action === "reject"}
          onConfirm={() =>
            target.action === "approve"
              ? approve.mutate(target.row)
              : reject.mutate(target.row)
          }
          onClose={() => setTarget(null)}
          data-testid="admin-access-request-confirm"
        />
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// E5 / E6 / E7 — Invites panel
// ---------------------------------------------------------------------------

function InvitesPanel({
  teams,
  teamsLoading,
}: {
  teams: InviteTeamOption[];
  teamsLoading: boolean;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const teamIdsKey = useMemo(
    () => teams.map((team) => team.id).sort().join("|"),
    [teams],
  );
  const teamNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const team of teams) map.set(team.id, team.name);
    return map;
  }, [teams]);
  const query = useQuery({
    queryKey: unscopedKey("admin-people-invites", { teamIds: teamIdsKey }),
    queryFn: async () => {
      const perTeam = await Promise.all(
        teams.map((team) => getTeamInvites(team.id)),
      );
      return perTeam.flat().sort((a, b) => {
        const aCreated = Number(a.created_at ?? 0);
        const bCreated = Number(b.created_at ?? 0);
        if (aCreated !== bCreated) return bCreated - aCreated;
        return b.id.localeCompare(a.id);
      });
    },
    enabled: teams.length > 0,
  });
  const [composerOpen, setComposerOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<TeamInviteRow | null>(null);
  // E7 — dev-token surface: stash rows that came back from a
  // create/resend with `invite_token` so the operator can copy them.
  // We hold the full row optimistically too — if a fresh create
  // hasn't been picked up by the list refetch yet (the row is
  // brand-new), the optimistic copy keeps the dev-link visible
  // until the GET catches up. Keyed by invite id so a refresh
  // overwrites the prior URL.
  const [devTokens, setDevTokens] = useState<Record<string, string>>({});
  const [optimisticRows, setOptimisticRows] = useState<TeamInviteRow[]>([]);

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: unscopedKey("admin-people-invites"),
    });

  const resend = useMutation({
    mutationFn: (invite: TeamInviteRow) => resendTeamInvite(invite.id, invite.team_id),
    onSuccess: (row) => {
      if (row.invite_token) {
        setDevTokens((m) => ({ ...m, [row.id]: row.invite_token! }));
      }
      toast.success("Invite resent");
      void invalidate();
    },
    onError: (err) => {
      toast.error("Couldn't resend invite", { description: formatErr(err) });
    },
  });

  // Merge optimistic rows + server list, deduped by id (server
  // wins once the GET resolves). Re-review P1 defense-in-depth:
  // filter optimistic rows by the currently manageable team ids so
  // a stale entry from a different tenant can never leak into the
  // rendered list.
  const visibleRows: TeamInviteRow[] = useMemo(() => {
    const fromServer = (query.data ?? []).filter(isInviteVisibleInPendingList);
    const knownIds = new Set(fromServer.map((r) => r.id));
    const allowedTeamIds = new Set(teams.map((team) => team.id));
    const stillPending = optimisticRows.filter(
      (r) =>
        allowedTeamIds.has(r.team_id) &&
        !knownIds.has(r.id) &&
        isInviteVisibleInPendingList(r),
    );
    return [...stillPending, ...fromServer];
  }, [query.data, optimisticRows, teams]);

  return (
    <section
      data-testid="admin-people-invites"
      className="flex flex-col gap-2 rounded border border-rule bg-surface shadow-sm p-3"
    >
      <header className="flex items-baseline justify-between">
        <h3 className="text-sm font-display uppercase tracking-wider text-ink">
          Invites
        </h3>
        <AdminIconButton
          icon={Plus}
          label="Send invite"
          size="md"
          tone="accent"
          onClick={() => setComposerOpen(true)}
          data-testid="admin-people-invite-new"
        />
      </header>

      {query.error ? (
        <p
          data-testid="admin-people-invites-error"
          className="text-destructive font-mono text-xs"
        >
          Failed to load invites.
        </p>
      ) : null}

      {visibleRows.length === 0 && !query.isLoading ? (
        <p
          data-testid="admin-people-invites-empty"
          className="text-xs font-mono text-ink-muted"
        >
          {teamsLoading
            ? "Loading teams…"
            : teams.length === 0
              ? "No manageable teams available."
              : "No invites sent yet."}
        </p>
      ) : null}

      {visibleRows.length > 0 ? (
        <ul className="flex flex-col gap-1">
          {visibleRows.map((invite) => (
            <InviteRow
              key={invite.id}
              invite={invite}
              teamName={teamNameById.get(invite.team_id) ?? invite.team_id}
              devTokenUrl={devTokens[invite.id]}
              onResend={() => resend.mutate(invite)}
              onRevoke={() => setRevokeTarget(invite)}
              resendPending={resend.isPending && resend.variables?.id === invite.id}
            />
          ))}
        </ul>
      ) : null}

      {composerOpen ? (
        <InviteComposerModal
          teams={teams}
          teamsLoading={teamsLoading}
          onClose={() => setComposerOpen(false)}
          onSuccess={(row) => {
            if (row.invite_token) {
              setDevTokens((m) => ({ ...m, [row.id]: row.invite_token! }));
            }
            // Optimistic insert — the dev-link surfaces immediately
            // even before the list refetch resolves. Once the GET
            // returns, the server row replaces this entry (matched
            // by id, deduped in `visibleRows`).
            setOptimisticRows((rows) => [row, ...rows.filter((r) => r.id !== row.id)]);
            setComposerOpen(false);
            void invalidate();
          }}
        />
      ) : null}

      {revokeTarget ? (
        <RevokeInviteConfirm
          teamId={revokeTarget.team_id}
          invite={revokeTarget}
          onClose={() => setRevokeTarget(null)}
          onSuccess={() => {
            setRevokeTarget(null);
            void invalidate();
          }}
        />
      ) : null}
    </section>
  );
}

function InviteRow({
  invite,
  teamName,
  devTokenUrl,
  onResend,
  onRevoke,
  resendPending,
}: {
  invite: TeamInviteRow;
  teamName: string;
  devTokenUrl: string | undefined;
  onResend: () => void;
  onRevoke: () => void;
  resendPending: boolean;
}) {
  const status = inviteDisplayStatus(invite, Date.now());
  const canManageInvite = isInviteActionable(invite);
  return (
    <li
      data-testid="admin-people-invite-row"
      data-invite-id={invite.id}
      data-invite-status={status}
      className="rounded border border-rule/60 bg-elevated px-2 py-1.5"
    >
      <div className="grid grid-cols-[1fr_auto_auto] gap-2 items-center text-xs">
        <div className="min-w-0">
          <div className="text-ink truncate">{invite.email}</div>
          <div className="flex flex-wrap items-center gap-1 text-[0.6rem] font-mono text-ink-muted">
            <span
              data-testid="admin-people-invite-row-team"
              data-team-id={invite.team_id}
              className="rounded bg-surface px-1 py-0.5 text-ink"
            >
              {teamName}
            </span>
            <span>{expiryLabel(invite.expires_at)}</span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <InviteRolePill role={invite.role} />
          <InviteStatusPill status={status} />
          <DeliveryPill
            status={invite.last_delivery_status ?? null}
            isPending={canManageInvite}
          />
        </div>
        <div className="inline-flex gap-1">
          {canManageInvite ? (
            <AdminIconButton
              icon={Send}
              label={resendPending ? "Resending invite" : "Resend invite"}
              onClick={onResend}
              disabled={resendPending}
              data-testid="admin-people-invite-resend"
            />
          ) : null}
          {canManageInvite ? (
            <AdminIconButton
              icon={Ban}
              label="Revoke invite"
              onClick={onRevoke}
              data-testid="admin-people-invite-revoke"
              tone="destructive"
            />
          ) : null}
        </div>
      </div>
      {/* E7 — dev-token copy-link, rendered ONCE per row on the row
       * that just got a fresh token (never logged, never in the
       * success banner — gated control only). */}
      {devTokenUrl ? (
        <div
          data-testid="admin-people-invite-dev-link"
          data-invite-id={invite.id}
          className="mt-1.5 flex items-center gap-1 text-[0.6rem] font-mono"
        >
          <span className="text-ink-muted">Dev invite URL:</span>
          <code className="text-ink break-all">{devTokenUrl}</code>
          <AdminIconButton
            icon={Copy}
            label="Copy invite link"
            onClick={() =>
              navigator.clipboard?.writeText(buildInviteUrl(devTokenUrl))
            }
            data-testid="admin-people-invite-dev-copy"
          />
        </div>
      ) : null}
    </li>
  );
}

export function inviteDisplayStatus(invite: TeamInviteRow, nowMs = Date.now()): string {
  if (invite.status === "pending" && invite.expires_at != null && invite.expires_at * 1000 < nowMs) {
    return "expired";
  }
  return invite.status || "pending";
}

export function isInviteActionable(invite: TeamInviteRow): boolean {
  return invite.status === "pending";
}

export function isInviteVisibleInPendingList(invite: TeamInviteRow): boolean {
  return invite.status === "pending";
}

function InviteStatusPill({ status }: { status: string }) {
  const tone =
    status === "pending"
      ? "border-accent text-accent"
      : status === "accepted"
        ? "border-good/40 text-good"
        : status === "revoked"
          ? "border-destructive/40 text-destructive"
          : "border-rule text-ink-muted";
  return (
    <span
      data-testid="admin-people-invite-status-pill"
      data-status={status}
      className={`rounded border bg-surface px-1.5 py-0.5 text-[0.55rem] font-display uppercase tracking-wider ${tone}`}
    >
      {status}
    </span>
  );
}

function InviteRolePill({ role }: { role: string }) {
  const colors: Record<string, string> = {
    team_admin: "border-accent text-accent",
    coach: "border-good/40 text-good",
    assistant_coach: "border-good/40 text-good",
    player: "border-rule text-ink",
    guardian: "border-rule text-ink-muted",
  };
  const tone = colors[role] ?? "border-rule text-ink-muted";
  const label =
    INVITE_ROLES.find((r) => r.value === role)?.label ?? role.replace("_", " ");
  return (
    <span
      data-testid="admin-people-role-pill"
      data-role={role}
      className={`rounded border bg-surface px-1.5 py-0.5 text-[0.55rem] font-display uppercase tracking-wider ${tone}`}
    >
      {label}
    </span>
  );
}

// E5 — Delivery pill enumerates the four legacy `last_delivery_status`
// values: sent / disabled / not_configured / failed.
function DeliveryPill({
  status,
  isPending,
}: {
  status: string | null;
  isPending: boolean;
}) {
  if (!isPending) return null;
  const map: Record<string, { label: string; tone: string }> = {
    sent: { label: "Email sent", tone: "border-good/40 text-good" },
    disabled: { label: "Email off", tone: "border-rule text-ink-muted" },
    not_configured: {
      label: "Email not configured",
      tone: "border-rule text-ink-muted",
    },
    failed: { label: "Send failed", tone: "border-destructive/40 text-destructive" },
  };
  const entry = status ? map[status] : null;
  if (!entry) {
    return (
      <span
        data-testid="admin-people-invite-delivery-pill"
        data-delivery="awaiting"
        className="rounded border border-rule bg-surface px-1.5 py-0.5 text-[0.55rem] font-display uppercase tracking-wider text-ink-muted"
      >
        awaiting
      </span>
    );
  }
  return (
    <span
      data-testid="admin-people-invite-delivery-pill"
      data-delivery={status}
      className={`rounded border bg-surface px-1.5 py-0.5 text-[0.55rem] font-display uppercase tracking-wider ${entry.tone}`}
    >
      {entry.label}
    </span>
  );
}

function expiryLabel(expiresAt: number | null | undefined): string {
  if (!expiresAt) return "no expiry on file";
  const ms = expiresAt * 1000;
  const diff = ms - Date.now();
  if (diff <= 0) return "expired";
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days >= 1) return `expires in ${days}d`;
  const hours = Math.floor(diff / (1000 * 60 * 60));
  if (hours >= 1) return `expires in ${hours}h`;
  return "expires in <1h";
}

function buildInviteUrl(token: string): string {
  // The Tktic SPA's invite-accept landing is `/invite/{token}` (a
  // route registered in router.tsx in M9). For dev-token copies
  // we surface the same path — the M9 landing handles both the
  // signed-in accept and the create-account branches.
  if (typeof window === "undefined") return `/invite/${token}`;
  return `${window.location.origin}/invite/${token}`;
}

function InviteComposerModal({
  teams,
  teamsLoading,
  onClose,
  onSuccess,
}: {
  teams: InviteTeamOption[];
  teamsLoading: boolean;
  onClose: () => void;
  onSuccess: (row: TeamInviteRow) => void;
}) {
  const toast = useToast();
  const teamOptions = useMemo(
    () => [
      ...(teams.length > 1
        ? [{ value: "", label: "Pick a team", disabled: true }]
        : []),
      ...teams.map((team) => ({
        value: team.id,
        label: team.name,
      })),
    ],
    [teams],
  );
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("coach");
  const [selectedTeamId, setSelectedTeamId] = useState(
    teams.length === 1 ? teams[0]?.id ?? "" : "",
  );
  const [playerIds, setPlayerIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (teams.length === 1) {
      setSelectedTeamId(teams[0]?.id ?? "");
      return;
    }
    if (
      selectedTeamId &&
      !teams.some((team) => team.id === selectedTeamId)
    ) {
      setSelectedTeamId("");
    }
  }, [selectedTeamId, teams]);

  // Roster is fetched only when role is player/guardian and the modal
  // has a concrete selected team. This keeps player linking tied to
  // the invite's row-owned team rather than the admin shell scope.
  const rosterEnabled =
    !!selectedTeamId && (role === "player" || role === "guardian");
  const rosterQuery = useQuery({
    queryKey: unscopedKey("admin-people-invite-roster", {
      teamId: selectedTeamId || "none",
    }),
    queryFn: () =>
      apiFetch<{ players: Player[] }>("/api/coach/players", {
        scope: { teamId: selectedTeamId, seasonId: null },
      }),
    enabled: rosterEnabled,
  });
  const players: Player[] = rosterEnabled
    ? rosterQuery.data?.players ?? []
    : [];

  const mut = useMutation({
    mutationFn: () =>
      createTeamInvite({
        team_id: selectedTeamId,
        email,
        role,
        player_ids:
          rosterEnabled ? playerIds : undefined,
      }),
    onSuccess: (row) => {
      toast.success("Invite sent");
      onSuccess(row);
    },
    onError: (err) => {
      setError(formatErr(err));
      toast.error("Couldn't send invite", { description: formatErr(err) });
    },
  });

  return (
    <ConfirmDialog
      title="Send invite"
      message={
        <div className="flex flex-col gap-2">
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
              Team
            </span>
            <TkticSelect
              value={selectedTeamId}
              onChange={(next) => {
                setSelectedTeamId(next);
                setPlayerIds([]);
              }}
              options={teamOptions}
              disabled={teamsLoading || teams.length <= 1}
              placeholder={teamsLoading ? "Loading teams…" : "Pick a team"}
              ariaLabel="Invite team"
              size="sm"
              data-testid="admin-people-invite-team"
            />
          </label>

          <label className="flex flex-col gap-1 text-xs">
            <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
              Email
            </span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-people-invite-email"
              required
              className="rounded border border-rule bg-surface px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </label>

          <div
            role="group"
            aria-label="Role"
            data-testid="admin-people-invite-role"
            className="flex flex-col gap-1"
          >
            <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
              Role
            </span>
            <div className="flex flex-wrap gap-1">
              {INVITE_ROLES.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => {
                    setRole(r.value);
                    if (r.value !== "player" && r.value !== "guardian") {
                      setPlayerIds([]);
                    }
                  }}
                  data-testid={`admin-people-invite-role-${r.value}`}
                  data-active={role === r.value ? "true" : undefined}
                  className="rounded border px-3 py-1 text-xs font-display uppercase tracking-wider transition-colors border-rule text-ink-muted hover:text-ink hover:bg-field data-[active=true]:border-accent data-[active=true]:bg-accent data-[active=true]:text-accent-ink"
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          {rosterEnabled ? (
            <div className="flex flex-col gap-1">
              <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
                Link to players
              </span>
              {players.length === 0 ? (
                <p className="text-[0.6rem] font-mono text-ink-muted">
                  {rosterQuery.isLoading
                    ? "Loading roster…"
                    : "No players on roster yet."}
                </p>
              ) : (
                <div
                  data-testid="admin-people-invite-player-list"
                  className="flex flex-col gap-0.5 max-h-40 overflow-auto rounded border border-rule bg-elevated p-1"
                >
                  {players.map((p) => {
                    const selected = playerIds.includes(p.id);
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => {
                          setPlayerIds((ids) =>
                            selected
                              ? ids.filter((x) => x !== p.id)
                              : [...ids, p.id],
                          );
                        }}
                        data-testid={`admin-people-invite-player-${p.id}`}
                        data-active={selected ? "true" : undefined}
                        className="flex items-center gap-1 w-full rounded px-1 py-0.5 text-xs text-left transition-colors hover:bg-field data-[active=true]:text-accent"
                      >
                        <span className="text-ink">
                          {p.display_name || p.id}
                          {p.jersey_number ? (
                            <span className="text-ink-muted">
                              {" "}
                              #{p.jersey_number}
                            </span>
                          ) : null}
                        </span>
                        {selected ? <span className="ml-auto text-accent">✓</span> : null}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ) : !selectedTeamId && (role === "player" || role === "guardian") ? (
            <p
              data-testid="admin-people-invite-player-scope-note"
              className="rounded border border-rule bg-elevated px-2 py-1 text-[0.6rem] font-mono text-ink-muted"
            >
              Pick a team before linking players.
            </p>
          ) : null}

          {error ? (
            <p
              data-testid="admin-people-invite-error"
              className="text-destructive font-mono text-xs"
            >
              {error}
            </p>
          ) : null}
        </div>
      }
      confirmLabel={mut.isPending ? "Sending…" : "Send invite"}
      onConfirm={() => {
        setError(null);
        if (!selectedTeamId) {
          setError("Pick a team.");
          return;
        }
        if (!email.trim() || email.length < 3) {
          setError("Email is required.");
          return;
        }
        mut.mutate();
      }}
      onClose={onClose}
      data-testid="admin-people-invite-composer"
    />
  );
}

function RevokeInviteConfirm({
  teamId,
  invite,
  onClose,
  onSuccess,
}: {
  teamId: string;
  invite: TeamInviteRow;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: () => revokeTeamInvite(invite.id, teamId),
    onSuccess: () => {
      toast.success("Invite revoked");
      onSuccess();
    },
    onError: (err) => {
      setError(formatErr(err));
      toast.error("Couldn't revoke invite", { description: formatErr(err) });
    },
  });
  return (
    <ConfirmDialog
      title="Revoke invite?"
      message={
        <>
          <p>
            The invite to <strong>{invite.email}</strong> will be cancelled.
            Any pending link becomes invalid.
          </p>
          {error ? (
            <p
              data-testid="admin-people-invite-revoke-error"
              className="mt-2 text-destructive font-mono text-xs"
            >
              {error}
            </p>
          ) : null}
        </>
      }
      confirmLabel={mut.isPending ? "Revoking…" : "Revoke invite"}
      danger
      onConfirm={() => {
        setError(null);
        mut.mutate();
      }}
      onClose={onClose}
      data-testid="admin-people-invite-revoke-confirm"
    />
  );
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function formatErr(err: unknown): string {
  if (err instanceof ApiError) {
    return typeof err.detail === "string" ? err.detail : `HTTP ${err.status}`;
  }
  return "Request failed";
}

/** Piece 2 of the consent-visibility plan: the staff answer to "who has
 * consented, to what, and when" — per-player evidence rows plus the
 * withdrawal queue, per team. Audience matches the server gate
 * (`roster:manage`: club admin + operator); the payload is roster-tier
 * data and never carries the operator's consent note. */
function formatConsentDate(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function TeamConsentsPanel({ teams }: { teams: InviteTeamOption[] }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [teamId, setTeamId] = useState("");
  // Fall back when the selected team leaves the manageable set (a
  // refetch can shrink it); otherwise a stale id keeps 403ing and the
  // selector is hidden at length 1, leaving no way to recover.
  const activeTeamId =
    (teamId && teams.some((t) => t.id === teamId) ? teamId : "") ||
    teams[0]?.id ||
    "";
  const query = useQuery({
    queryKey: unscopedKey(`admin-team-consents-${activeTeamId}`),
    queryFn: () => getAdminTeamConsents(activeTeamId),
    enabled: !!activeTeamId,
    staleTime: 30_000,
  });
  const resolve = useMutation({
    mutationFn: (withdrawalId: string) =>
      resolveConsentWithdrawal(activeTeamId, withdrawalId),
    onSuccess: async () => {
      toast.success("Withdrawal marked handled");
      await queryClient.invalidateQueries({
        queryKey: unscopedKey(`admin-team-consents-${activeTeamId}`),
      });
    },
    onError: (err) =>
      toast.error("Couldn't resolve the withdrawal", { description: formatErr(err) }),
  });

  if (teams.length === 0) return null;
  const overview = query.data;
  const pendingWithdrawals =
    overview?.withdrawals.filter((w) => w.status === "pending") ?? [];

  return (
    <section
      id="admin-people-consents"
      data-testid="admin-team-consents"
      className="flex flex-col gap-3"
    >
      <header className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h3 className="text-base font-display uppercase tracking-wider text-ink">
            Consent
          </h3>
          <p className="mt-1 text-sm text-ink-muted">
            Who has consented, when, and at which document version — the
            evidence behind the roster's consent pill.
          </p>
        </div>
        {teams.length > 1 ? (
          <TkticSelect
            ariaLabel="Consent team"
            data-testid="admin-consent-team-select"
            size="sm"
            value={activeTeamId}
            onChange={setTeamId}
            options={teams.map((t) => ({ value: t.id, label: t.name }))}
          />
        ) : null}
      </header>
      {query.isError ? (
        <p className="rounded border border-destructive/40 bg-destructive/10 p-3 font-mono text-xs text-destructive">
          Failed to load consent records.
        </p>
      ) : !overview ? (
        <p className="rounded border border-rule bg-surface p-3 text-sm text-ink-muted">
          Loading consent records…
        </p>
      ) : (
        <>
          {pendingWithdrawals.length > 0 ? (
            <div className="rounded border border-flag/40 bg-flag/5 p-3">
              <p className="text-xs font-display uppercase tracking-wider text-flag">
                Withdrawal requests
              </p>
              <div className="mt-2 space-y-2">
                {pendingWithdrawals.map((w) => (
                  <div
                    key={w.id}
                    data-testid="admin-consent-withdrawal-row"
                    className="flex flex-wrap items-center justify-between gap-2 rounded border border-rule bg-surface px-3 py-2"
                  >
                    <div className="min-w-0 text-sm text-ink">
                      <span className="font-medium">{w.requested_by}</span>
                      {w.player_name ? ` · ${w.player_name}` : " · whole team"}
                      {w.message ? (
                        <p className="mt-0.5 text-xs text-ink-muted">{w.message}</p>
                      ) : w.message_withheld ? (
                        <p className="mt-0.5 text-xs italic text-ink-muted">
                          The family included a message for the club. Open this
                          on the club's own host to read it.
                        </p>
                      ) : null}
                      <p className="mt-0.5 font-mono text-[0.65rem] text-ink-muted">
                        {formatConsentDate(w.created_at)}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      data-testid="admin-consent-withdrawal-resolve"
                      disabled={resolve.isPending}
                      title="Mark handled AFTER removing the requested data — this button deletes nothing."
                      onClick={() => resolve.mutate(w.id)}
                    >
                      Mark handled
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          <div className="overflow-hidden rounded border border-rule bg-surface">
            {overview.players.map((p) => (
              <div
                key={p.id}
                data-testid="admin-consent-row"
                data-status={p.consent_status}
                className="flex flex-wrap items-center justify-between gap-2 border-b border-rule px-3 py-2 last:border-b-0"
              >
                <div className="min-w-0">
                  <span className="text-sm font-medium text-ink">
                    {p.display_name}
                  </span>
                  {p.jersey_number ? (
                    <span className="ml-1 font-mono text-xs text-ink-muted">
                      #{p.jersey_number}
                    </span>
                  ) : null}
                  <p className="mt-0.5 text-xs text-ink-muted">
                    {p.consent
                      ? `${
                          p.consent.consented_by ?? "Recorded by the club"
                        } · ${formatConsentDate(p.consent.created_at)} · v ${p.consent.version} · ${
                          p.consent.source === "email"
                            ? "club-recorded (email)"
                            : "in app"
                        }`
                      : "Nothing on file"}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  {/* A withdrawal by one guardian no longer voids a
                      co-guardian's consent, so the status can read
                      "current" while a request is open. Surface it here
                      or the parent who asked gets no follow-up. */}
                  {p.withdrawal_pending ? (
                    <span
                      data-testid="admin-consent-withdrawal-pending"
                      title="A guardian has asked to withdraw. Another guardian's consent currently keeps this player covered — follow up with the family."
                      className="inline-flex rounded-full border border-flag/40 px-2 py-0.5 font-mono text-[0.65rem] uppercase tracking-wider text-flag"
                    >
                      Withdrawal asked
                    </span>
                  ) : null}
                  <span
                    data-status={p.consent_status}
                    className="inline-flex rounded-full border border-rule px-2 py-0.5 font-mono text-[0.65rem] uppercase tracking-wider text-ink-muted data-[status=current]:border-good/40 data-[status=current]:text-good data-[status=missing]:border-flag/40 data-[status=missing]:text-flag data-[status=stale]:border-flag/40 data-[status=stale]:text-flag data-[status=withdrawn]:border-flag data-[status=withdrawn]:text-flag"
                  >
                    {p.consent_status === "stale" ? "Re-consent" : p.consent_status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}
